/**
 * エラーダイアログの表示項目と、「すべてコピー」の平文（#456）。
 *
 * @remarks
 * 表示とコピーは同じ項目・同じ順に並べる（スクリーンショットと貼り付けのどちらで受け取っても
 * 同じに読める）。違いはスタックだけで、表示は 1 枚に収まるよう整形し（`displayStackLines`）、
 * コピーには全文（元の位置に戻す前のものも）を入れる。
 */
import { NEXUS_TIME_ZONE } from "@/lib/business-time";
import type {
  ErrorReport,
  ErrorReportEnvironment,
  ErrorReportKind,
} from "@/lib/errors/report";
import { displayStackLines } from "@/lib/errors/stack";

/** 種類の表示名。 */
export const ERROR_KIND_LABELS: Record<ErrorReportKind, string> = {
  api: "API エラー",
  network: "通信エラー",
  runtime: "画面の処理中のエラー",
  render: "画面の表示中のエラー",
};

/** ダイアログの 1 項目。 */
export interface ErrorReportRow {
  /** 見出し。 */
  label: string;
  /** 値（複数行のときは改行区切り）。 */
  value: string;
  /** スタックなど等幅で出す複数行の値か。 */
  block?: boolean;
}

/**
 * 時刻を JST・ミリ秒つきで書く（`2026-10-04 14:23:05.123 (JST)`）。ログの `ts` と突き合わせる。
 *
 * @param date - 時刻。
 * @param withDate - 日付も書くか（既定 true）。
 */
export function formatErrorTime(date: Date, withDate = true): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: NEXUS_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date);
  const v = new Map(parts.map((p) => [p.type, p.value]));
  const ms = String(date.getMilliseconds()).padStart(3, "0");
  const time = `${v.get("hour")}:${v.get("minute")}:${v.get("second")}.${ms}`;
  return withDate ? `${v.get("year")}-${v.get("month")}-${v.get("day")} ${time} (JST)` : time;
}

/**
 * User-Agent を「ブラウザ 版 / OS」に縮める（表示用。コピーには全文を入れる）。
 *
 * @param userAgent - `navigator.userAgent`。
 */
export function summarizeUserAgent(userAgent: string): string {
  // iOS の Chrome・Edge・Firefox は版を CriOS/・EdgiOS/・FxiOS/ で書く
  const browsers: [string, RegExp][] = [
    ["Edge", /Edg(?:iOS|A)?\/(\d+)/],
    ["Firefox", /(?:Firefox|FxiOS)\/(\d+)/],
    ["Chrome", /(?:Chrome|CriOS)\/(\d+)/],
    ["Safari", /Version\/(\d+).*Safari/],
  ];
  // UA の文字列に別の OS の名前が入るもの（iOS の「like Mac OS X」・Android の「Linux」）を先に置く
  const systems: [string, RegExp][] = [
    ["Windows", /Windows NT/],
    ["iOS", /iPhone|iPad|iPod/],
    ["macOS", /Mac OS X/],
    ["Android", /Android/],
    ["Linux", /Linux/],
  ];
  let browser = "不明なブラウザ";
  for (const [name, pattern] of browsers) {
    const match = pattern.exec(userAgent);
    if (match) {
      browser = `${name} ${match[1]}`;
      break;
    }
  }
  const os = systems.find(([, pattern]) => pattern.test(userAgent))?.[0] ?? "不明な OS";
  return `${browser} / ${os}`;
}

function versionLabel(env: ErrorReportEnvironment): string | null {
  const commit = env.commit ? env.commit.slice(0, 7) : null;
  if (env.version && commit) return `${env.version} (${commit})`;
  return env.version ?? commit;
}

/**
 * 1 件の表示項目（値の無い項目は出さない）。
 *
 * @param report - 1 件。
 * @param env - 利用者と版。
 * @param userAgent - `navigator.userAgent`（無ければ出さない）。
 * @param forCopy - コピー用か（スタックを全文にし、User-Agent を縮めない）。
 */
export function errorReportRows(
  report: ErrorReport,
  env: ErrorReportEnvironment,
  userAgent: string | null,
  forCopy = false,
): ErrorReportRow[] {
  const rows: ErrorReportRow[] = [];
  const add = (label: string, value: string | null | undefined, block = false) => {
    if (value != null && value !== "") rows.push({ label, value, block });
  };
  const repeated =
    report.count > 1
      ? `（同じエラーが ${report.count} 回・最後は ${formatErrorTime(report.lastOccurredAt, false)}）`
      : "";
  add("発生日時", `${formatErrorTime(report.occurredAt)}${repeated}`);
  add("エラーID", report.requestId);
  add(
    "種類",
    `${ERROR_KIND_LABELS[report.kind]}${report.errorName ? `（${report.errorName}）` : ""}`,
  );
  if (report.request) add("リクエスト", `${report.request.method} ${report.request.url}`);
  if (report.status != null && report.kind === "api") {
    add(
      "応答",
      [`${report.status} ${report.statusText ?? ""}`.trim(), report.code].filter(Boolean).join("  "),
    );
  }
  add("メッセージ", report.message);
  add("サーバーの記録時刻", report.serverTimestamp);
  add("サーバーの例外", report.serverErrorType);
  if (report.serverStack && report.serverStack.length > 0) {
    add("サーバーのスタックトレース", report.serverStack.join("\n"), true);
  }
  add("応答の本文（先頭）", report.bodyExcerpt, true);
  const stack = report.resolvedClientStack ?? report.clientStack;
  if (stack) {
    const lines = forCopy
      ? stack.split("\n")
      : displayStackLines(stack, report.resolvedClientStack != null);
    add("画面のスタックトレース", lines.join("\n"), true);
    if (forCopy && report.resolvedClientStack && report.clientStack) {
      add("画面のスタックトレース（元の位置に戻す前）", report.clientStack, true);
    }
  }
  add("digest", report.digest);
  add("画面", report.page);
  add("利用者", env.email);
  add("版", versionLabel(env));
  if (userAgent) add("ブラウザ", forCopy ? userAgent : summarizeUserAgent(userAgent));
  return rows;
}

/**
 * 「すべてコピー」の平文（全件・スタックは全文）。
 *
 * @param reports - ダイアログの全件（新しい順）。
 * @param env - 利用者と版。
 * @param userAgent - `navigator.userAgent`。
 */
export function formatErrorReportsText(
  reports: readonly ErrorReport[],
  env: ErrorReportEnvironment,
  userAgent: string | null,
): string {
  const blocks = reports.map((report, index) => {
    const rows = errorReportRows(report, env, userAgent, true).map((row) =>
      row.block
        ? `${row.label}:\n${row.value
            .split("\n")
            .map((l) => `  ${l.trimStart()}`)
            .join("\n")}`
        : `${row.label}: ${row.value}`,
    );
    const heading = reports.length > 1 ? `=== ${index + 1} 件目 ===\n` : "";
    return `${heading}${rows.join("\n")}`;
  });
  return [`Nexus エラー報告（${reports.length} 件）`, ...blocks].join("\n\n");
}
